import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { asyncScheduler, combineLatest, Subject, takeUntil } from 'rxjs';
import { observeOn } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import type { UserIdMap } from '@core/account-data';
import type { Requisition, RequisitionAssignedFlow } from '@core/requisitions-types';

import { RequisitionsDetailsContainerComponent } from '../../../containers/single-requisition/requisitions-details-container/requisitions-details-container.component';
import { DetailsPageDataService } from '../../../data/single-requisition/details-page-data.service';

@Component({
  selector: 'rt-requisitions-details-page',
  templateUrl: './details-page.component.html',
  styleUrls: ['./details-page.component.less'],
  imports: [RequisitionsDetailsContainerComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DetailsPageDataService],
})
export class DetailsPageComponent {
  private readonly data = inject(DetailsPageDataService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public requisition: Requisition;
  public adminsMap: UserIdMap;
  public approvalFlow: RequisitionAssignedFlow | null = null;
  public isDepartmentsMode: boolean;
  public allApproversDeleted = false;
  public approversWithoutAccessCount = 0;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    combineLatest([
      this.data.requisition$,
      this.data.adminsMap$,
      this.data.approvalFlow$,
      this.data.isDepartmentsMode$,
    ])
      .pipe(observeOn(asyncScheduler), takeUntil(this.ngOnDestroy$))
      .subscribe(([requisition, adminsMap, approvalFlow, isDepartmentsMode]) => {
        this.requisition = requisition;
        this.adminsMap = adminsMap;
        this.approvalFlow = approvalFlow;
        this.isDepartmentsMode = isDepartmentsMode;
        if (approvalFlow) {
          const approverIds = [...new Set(approvalFlow.steps.map(step => step.approverIds).flat())];
          this.allApproversDeleted = approverIds.every(id => !adminsMap[id]);
          this.approversWithoutAccessCount = approverIds.filter(id => {
            if (!adminsMap[id]) return false;
            return !adminsMap[id].roleAbilities.includes(Ability.ViewRequisitions);
          }).length;
        }
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
